import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { cache } from '../lib/cache';

interface SignedImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  path: string;
  bucket?: string;
  fallback?: React.ReactNode;
  cacheBust?: string | number;
}

export const SignedImage: React.FC<SignedImageProps> = ({ path, bucket = 'cpgev', fallback = null, cacheBust, ...props }) => {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    const fetchSignedUrl = async () => {
      if (!path) {
        setLoading(false);
        return;
      }
      
      const cacheKey = `signed_url_${bucket}_${path}${cacheBust ? `_${cacheBust}` : ''}`;
      const cachedUrl = cache.get<string>(cacheKey);
      
      if (cachedUrl) {
        setUrl(cachedUrl);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(false);
        // Sanitize path: remove leading slash if present
        const sanitizedPath = path.startsWith('/') ? path.substring(1) : path;
        
        // Use our server-side proxy to bypass RLS and handle special characters
        const response = await fetch(`/api/storage/signed-url?bucket=${bucket}&path=${encodeURIComponent(sanitizedPath)}`);
        
        if (!response.ok) {
          const errData = await response.json();
          if (response.status === 404) {
            console.warn(`SignedImage: Object not found at path "${sanitizedPath}" in bucket "${bucket}"`);
          } else {
            console.error('Error from storage proxy:', errData.error);
          }
          if (isMounted) setError(true);
          return;
        }

        const data = await response.json();

        if (data?.signedUrl && isMounted) {
          setUrl(data.signedUrl);
          cache.set(cacheKey, data.signedUrl);
        }
      } catch (err) {
        console.error('Unexpected error fetching signed URL:', err);
        if (isMounted) setError(true);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchSignedUrl();
    
    return () => {
      isMounted = false;
    };
  }, [path, bucket, cacheBust]);

  useEffect(() => {
    if (error && props.onError) {
      props.onError({} as any);
    }
  }, [error, props.onError]);

  if (loading) {
    return <div className={props.className} />;
  }

  if (error || !url) {
    return <>{fallback}</>;
  }

  return (
    <img 
      {...props} 
      src={url} 
      referrerPolicy="no-referrer" 
    />
  );
};
